import type { Region } from 'react-native-maps';

/** Rough miles per degree of latitude; longitude is scaled by cos(latitude). */
const MILES_PER_DEGREE_LAT = 69;

/** Initial camera span: the map opens zoomed to ~100 miles across (product spec). */
export const INITIAL_SPAN_MILES = 100;

/** Camera span when focusing a single coach (marker tap / Nearest-list selection): zoom in
 *  tight enough that the picked venue is clearly centred. */
export const FOCUS_SPAN_MILES = 8;

/** Supercluster tuning: 60px grouping radius, stop clustering once zoomed past 16. */
export const CLUSTER_RADIUS = 60;
export const CLUSTER_MAX_ZOOM = 16;

/** Below this lat/long span (~55m) the cluster's members effectively share one venue and
 *  can never separate by zooming; we ease in instead of fitting to a zero-size box. */
export const MIN_SPLIT_SPAN_DEG = 0.0005;

/** Build a Region centred on `center` spanning roughly `miles` vertically. */
export const regionForSpanMiles = (
  center: { latitude: number; longitude: number },
  miles: number,
): Region => {
  const latitudeDelta = miles / MILES_PER_DEGREE_LAT;
  const longitudeDelta =
    latitudeDelta / Math.max(0.01, Math.cos((center.latitude * Math.PI) / 180));
  return { ...center, latitudeDelta, longitudeDelta };
};

/** Neutral fallback view when we don't have the guardian's real location yet (or were
 *  denied it). Roughly the U.S. Kids Golf HQ area — just somewhere to point the camera. */
export const DEFAULT_REGION: Region = regionForSpanMiles(
  { latitude: 35.1954, longitude: -79.4695 },
  INITIAL_SPAN_MILES,
);

/** Map the visible region to the integer zoom level Supercluster expects. */
export const regionToZoom = (region: Region): number =>
  Math.min(20, Math.max(0, Math.round(Math.log2(360 / region.longitudeDelta))));

/** West/South/East/North bounds of the visible region, as Supercluster's bbox. */
export const regionToBBox = (region: Region): GeoJSON.BBox => [
  region.longitude - region.longitudeDelta / 2,
  region.latitude - region.latitudeDelta / 2,
  region.longitude + region.longitudeDelta / 2,
  region.latitude + region.latitudeDelta / 2,
];
